// ============================================================
// ATTENDANCE MANAGEMENT v3  (updated 2026-06-17)
// Previous implementation preserved at bottom as comment block.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Avatar, ConfigProvider, DatePicker, Input,
    Modal, Popover, Select, TimePicker, Tooltip, Pagination,
} from 'antd';
import {
    CoffeeOutlined, DownloadOutlined, FileTextOutlined,
    InfoCircleOutlined, LeftOutlined, PrinterOutlined,
    RightOutlined, UserOutlined, PlusOutlined, CheckSquareOutlined,
} from '@ant-design/icons';
import { MdLunchDining } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import { ApiCaller, Colors, Utils } from '../../config';
import { AttendenceManagementAction } from '../../store/actions';
import { convertToPdf } from '../../config/util/pdfConverter';
import Circular from '../../components/Loader/Circular';
import { PaginatedSelect, buildPropertyOption } from '../../components';
import { getPropertiesPage } from '../../config/getApis';
import useColors from '../../hooks/useColors';
import EditTimeDrawer from './EditTimeDrawer';
import './AttendenceManagement.css';
import { weekdays } from 'moment';
import { localToUtc, formatUtcToLocalDisplay, utcToLocal, utcDiffInHours, getLocalTimeZone } from '../../config/util/dateTimeTemporal';

// ── constants ─────────────────────────────────────────────────
const DAY_NUM = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
};

// column flex values (percentages that sum to ~82%; Difference column uses flex:1 for the rest)
const CW = {
    day: '15%',
    in: '11%',
    out: '14%',   // wider than Check-In: carries the overnight "+1d" badge
    total: '6%',
    details: '16%',
    worked: '9%',
    scheduled: '11%',
    diff: '11%',
};

// ── helpers ───────────────────────────────────────────────────
function getWeekFromDate(date, startDay = 'Sunday') {
    // Return selected date to selected date + 7 days (8 days total)
    const weekStart = dayjs(date);
    return {
        start: weekStart.format('YYYY-MM-DD'),
        end: weekStart.add(6, 'day').format('YYYY-MM-DD'),
    };
}

function generateWeekDays(start, end) {
    const days = [];
    let cur = dayjs(start);
    const endDate = dayjs(end);
    while (cur.isBefore(endDate) || cur.isSame(endDate, 'day')) {
        days.push(cur.format('YYYY-MM-DD'));
        cur = cur.add(1, 'day');
    }
    return days;
}

function toDecimalHours(minutes) {
    if (minutes == null || minutes === '') return '-';
    return (Number(minutes) / 60).toFixed(2);
}

// Check-In/Check-Out timestamps come from the API as UTC; this renders
// them in the viewer's local timezone (DST-safe, via Temporal).
function formatTS(ts, timeFormat) {
    if (!ts) return '-';
    const fmt = timeFormat === 'HH:mm' ? 'HH:mm' : 'hh:mm A';
    return formatUtcToLocalDisplay(ts, fmt);
}

// Check-In/Check-Out are absolute UTC instants, so which calendar day they
// land on depends on the viewer's timezone — the day-rollover must always be
// measured AFTER converting to local time, never on the raw UTC string.
function localDateOf(utcTs) {
    if (!utcTs) return null;
    const local = utcToLocal(utcTs);
    return local && local.isValid() ? local.format('YYYY-MM-DD') : null;
}

// Whole calendar days between two 'YYYY-MM-DD' local dates. Anchored at noon
// so a DST transition can never turn a 1-day gap into 0.
function dayDiff(fromDate, toDate) {
    if (!fromDate || !toDate) return 0;
    return dayjs(`${toDate}T12:00:00`).diff(dayjs(`${fromDate}T12:00:00`), 'day');
}

// How many calendar days after the check-in the check-out falls, in local
// time: 0 = same day, 1 = overnight shift that ends the next morning.
function checkOutDayOffset(checkInUtc, checkOutUtc) {
    return dayDiff(localDateOf(checkInUtc), localDateOf(checkOutUtc));
}

// Single source of truth for "is this shift approved" — case-insensitive
// since the backend's attendanceStatus casing isn't guaranteed.
function isRecordApproved(rec) {
    return String(rec?.attendanceStatus || '').toLowerCase() === 'approved';
}

// checkIn/checkOut are UTC ISO timestamps from the API — the diff itself is
// timezone-agnostic, but parsed via Temporal (see utcDiffInHours) for
// consistency with the rest of the Check-In/Check-Out handling. Despite the
// name, this returns hours (kept as-is to avoid touching every call site).
const calcWorkedMinutes = (checkIn, checkOut) => utcDiffInHours(checkIn, checkOut);

// Format minute-difference as "+2h 30m" / "-0h 45m"
function formatDiffHM(value) {
    if (value == null) return "-";
    const sign = value < 0 ? "-" : "+";
    const absValue = Math.abs(value);
    const hours = Math.floor(absValue);
    const minutes = Math.round((absValue - hours) * 60);
    return `${sign}${hours}h ${minutes.toString().padStart(2, '0')}m`;
}

// ── DetailBadge ───────────────────────────────────────────────
function DetailBadge({ icon, label }) {
    if (!label) return null;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            background: '#f0f2f5', border: '1px solid #d9d9d9',
            borderRadius: 4, padding: '1px 7px', fontSize: 11, color: '#444',
            whiteSpace: 'nowrap',
        }}>
            {icon}<span>{label}</span>
        </span>
    );
}

// ── OvernightInfo ─────────────────────────────────────────────
// Overnight shifts (e.g. check-in 7 Aug 9 PM, check-out 8 Aug 1 AM) are kept
// on the check-in's row so the day isn't split in two. This badge + info
// popover makes the hidden date shift explicit instead of leaving the reader
// to guess why the check-out looks "before" the check-in.
function OvernightInfo({ checkInUtc, checkOutUtc, offset, dateFormat, timeFormat, color }) {
    const [open, setOpen] = useState(false);
    const inLocal = utcToLocal(checkInUtc);
    const outLocal = utcToLocal(checkOutUtc);
    if (!outLocal || !outLocal.isValid() || offset <= 0) return null;

    const tf = timeFormat === 'HH:mm' ? 'HH:mm' : 'hh:mm A';
    const badge = offset === 1 ? '+1d' : `+${offset}d`;
    const dayLabel = offset === 1 ? 'Next day' : `${offset} days later`;

    const line = (label, val, extra) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ color: '#888' }}>{label}</span>
            <span style={{ fontWeight: 600, color: '#222', textAlign: 'right' }}>
                {val}{extra}
            </span>
        </div>
    );

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            placement="bottomRight"
            content={
                <div style={{ width: 268, fontSize: 12, lineHeight: 1.7 }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#222', marginBottom: 6 }}>
                        Overnight shift
                    </div>
                    {inLocal && inLocal.isValid() &&
                        line('Check-In', `${inLocal.format(dateFormat)} · ${inLocal.format(tf)}`)}
                    {line(
                        'Check-Out',
                        `${outLocal.format(dateFormat)} · ${outLocal.format(tf)}`,
                        <span style={{
                            marginLeft: 6, background: '#fff7e6', border: '1px solid #ffd591',
                            color: '#d46b08', borderRadius: 4, padding: '0 5px', fontSize: 10, fontWeight: 700,
                        }}>{dayLabel}</span>
                    )}
                    <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 8, paddingTop: 8, color: '#666' }}>
                        This check-out is on <strong>{outLocal.format('ddd, MMM D')}</strong>, but it belongs to the
                        shift that started on <strong>{inLocal && inLocal.isValid() ? inLocal.format('ddd, MMM D') : '—'}</strong>,
                        so it is listed on that day's row.
                        <div style={{ marginTop: 6 }}>
                            Editing it saves on <strong>{outLocal.format(dateFormat)}</strong> — the date stays
                            changeable in the picker.
                        </div>
                        <div style={{ marginTop: 6, color: '#aaa', fontSize: 11 }}>
                            All times in your local timezone ({getLocalTimeZone()}).
                        </div>
                    </div>
                </div>
            }
        >
            <span
                onClick={(e) => e.stopPropagation()}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer',
                    background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4,
                    padding: '0 4px', fontSize: 10, fontWeight: 700, color: '#d46b08',
                    lineHeight: '16px', whiteSpace: 'nowrap',
                }}
            >
                {badge}
                <InfoCircleOutlined style={{ fontSize: 11, color: color || '#d46b08' }} />
            </span>
        </Popover>
    );
}

// ── WeekSelector ──────────────────────────────────────────────
function WeekSelector({ start, end, weekStartDay, onChange }) {
    const [open, setOpen] = useState(false);
    const label = `${dayjs(start).format('MMM D')} - ${dayjs(end).format('D, YYYY')}`;
    const prev = () => {
        const ns = dayjs(start).subtract(7, 'day');
        onChange(ns.format('YYYY-MM-DD'), ns.add(6, 'day').format('YYYY-MM-DD'));
    };
    const next = () => {
        const ns = dayjs(start).add(7, 'day');
        onChange(ns.format('YYYY-MM-DD'), ns.add(6, 'day').format('YYYY-MM-DD'));
    };
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 2,
            border: '1px solid #d9d9d9', borderRadius: 6,
            background: '#fafafa', padding: '3px 6px',
        }}>
            <LeftOutlined
                style={{ fontSize: 10, cursor: 'pointer', color: '#666', padding: '0 2px' }}
                onClick={prev}
            />
            <DatePicker
                open={open}
                onOpenChange={setOpen}
                value={dayjs(start)}
                onChange={(date) => {
                    if (!date) return;
                    const r = getWeekFromDate(date, weekStartDay);
                    onChange(r.start, r.end);
                    setOpen(false);
                }}
                format={() => label}
                style={{
                    border: 'none', background: 'transparent', boxShadow: 'none',
                    padding: '0 2px', fontSize: 12, width: 148,
                }}
                bordered={false}
                suffixIcon={null}
                size="small"
            />
            <RightOutlined
                style={{ fontSize: 10, cursor: 'pointer', color: '#666', padding: '0 2px' }}
                onClick={next}
            />
        </div>
    );
}

// ── MAIN COMPONENT ────────────────────────────────────────────
const AttendenceManagement = () => {
    const { colorPrimary } = useColors();
    const dispatch = useDispatch();
    const location = useLocation();
    const contentRef = useRef(null);
    const printRef = useRef(null);

    // redux
    const { loader, attendenceData, schedulesData, sidebarUsersData, sidebarUsersLoader, paginate, meta, approving } =
        useSelector((s) => s.AttendenceManagementReducer);
    console.log('AttendenceManagement data', { attendenceData, meta, paginate });

    // Use backend-provided workWeek instead of frontend calculation
    const workWeek = meta?.workWeek || { startDate: dayjs().startOf('week').format('YYYY-MM-DD'), endDate: dayjs().endOf('week').format('YYYY-MM-DD'), startDay: 'Sunday' };

    const timeFormat = useSelector((s) => s.AppReducer?.timeFormat) || 'hh:mm A';
    const dateFormat = useSelector((s) => s.AppReducer?.dateFormat) || 'MM/DD/YYYY';
    const authUser = useSelector((s) => s.AppReducer?.user);

    const userRole = (authUser?.userRole?.[0] || '').toLowerCase();
    const canManualClock = ['admin', 'superadmin', 'supervisor', 'manager'].includes(userRole);

    // ── state ─────────────────────────────────────────────────
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        propertyId: '',
        scheduleId: '',
        userId: '',
        page: 1,
        perPage: 10,
    });
    const [userSearch, setUserSearch] = useState('');

    // property dropdown: searchable + loads the next page while scrolling.
    // Keeps the label of the selected property around, since it may not be on a
    // page the dropdown currently has loaded (or may be filtered out by a search).
    const [selectedPropOption, setSelectedPropOption] = useState(null);

    // Same option format as the shift screens: colour dot + "company -- property"
    const fetchPropertiesPage = useCallback(async ({ page, search }) => {
        const { data, hasMore } = await getPropertiesPage({ page, search });
        return { options: data.map(buildPropertyOption), hasMore };
    }, []);

    // inline cell editing
    const [editingCell, setEditingCell] = useState(null); // { date, field }
    const [savingCell, setSavingCell] = useState(null);   // { date, field }

    // row selection for approval (stores shiftUserId values)
    const [selectedIds, setSelectedIds] = useState(new Set());

    // row hover
    const [hoveredRow, setHoveredRow] = useState(null);
    const [hoveredDayRow, setHoveredDayRow] = useState(null);
    const [popoverDate, setPopoverDate] = useState(null);

    // legacy drawer (kept for compatibility)
    const [drawerStates, setDrawerStates] = useState({ checkoutTimeDrawer: false });
    const [editTimeData, setEditTimeData] = useState({ id: '', checkIn: '', checkOut: '' });

    // modals
    const [anotherEntryModal, setAnotherEntryModal] = useState({ open: false, date: null, checkIn: null, checkOut: null });
    const [timeOffModal, setTimeOffModal] = useState({ open: false, date: null, note: '' });
    const [notesModal, setNotesModal] = useState({ open: false, record: null, text: '' });
    const [breakModal, setBreakModal] = useState({ open: false, record: null, breakType: null, start: null, end: null });
    const [historyModal, setHistoryModal] = useState({ open: false, record: null, history: [], loading: false });

    // optimistic local edits: { "2026-06-17|in": "09:00", ... }
    const [localEdits, setLocalEdits] = useState({});

    // summary
    const [summary, setSummary] = useState({ regular: 0, overTime: 0, doubleOT: 0, timeOff: 0, paidTotal: 0, scheduledVsWorked: 0 });

    const is24h = timeFormat === 'HH:mm';
    const pickerFmt = is24h ? 'HH:mm' : 'h:mm A';

    // ── lifecycle ─────────────────────────────────────────────
    useEffect(() => {
        if (location.pathname !== '/attendence-management') {
            dispatch(AttendenceManagementAction.ClearAttendence());
        }
    }, [location.pathname]);

    useEffect(() => {
        const apiFilters = { ...filters };
        if (!apiFilters.userId) {
            apiFilters.perPage = 1000;
        }
        dispatch(AttendenceManagementAction.GetAttendence(apiFilters));
    }, [filters.startDate, filters.endDate, filters.propertyId, filters.scheduleId, filters.userId, filters.page, filters.perPage]);

    useEffect(() => {
        if (sidebarUsersData && sidebarUsersData.length > 0) {
            const currentInSidebar = sidebarUsersData.some(u => u.value === filters.userId);
            if (!filters.userId || !currentInSidebar) {
                handleFilterChange('userId', sidebarUsersData[0].value);
            }
        } else {
            if (filters.userId) {
                handleFilterChange('userId', '');
            }
        }
    }, [sidebarUsersData]);

    // /users/all is unpaginated and unfiltered — one fetch on mount is enough,
    // the search then runs on the frontend.
    useEffect(() => {
        dispatch(AttendenceManagementAction.GetSidebarUsers());
    }, []);

    useEffect(() => {
        // properties are no longer pulled from redux — the dropdown fetches
        // them page by page itself
        dispatch(AttendenceManagementAction.GetAttendenceSchedules());
    }, []);

    useEffect(() => {
        if (!attendenceData?.length || !filters.userId) {
            setSummary({ regular: 0, overTime: 0, doubleOT: 0, timeOff: 0, paidTotal: 0, scheduledVsWorked: 0 });
            return;
        }
        let worked = 0, overtime = 0, scheduled = 0, difference = 0;
        for (const r of attendenceData) {
            let rWorked = r.workedMinutes || 0;
            let rScheduled = r.requiredMinutes || 0;
            worked += rWorked;
            scheduled += rScheduled;
            let currentDiff = rWorked - rScheduled;
            difference += currentDiff;
            if (currentDiff > 0) overtime += currentDiff;
        }
        setSummary({
            regular: toDecimalHours(worked),
            overTime: toDecimalHours(overtime),
            doubleOT: 0,
            timeOff: 0,
            paidTotal: toDecimalHours(worked),
            scheduledVsWorked: toDecimalHours(difference),
        });
    }, [attendenceData, filters.userId]);

    // ── handlers ──────────────────────────────────────────────
    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };
            if (key !== 'page' && key !== 'perPage') {
                newFilters.page = 1;
            }
            if (key === 'propertyId') sessionStorage.setItem('attendance_property_id', value || '');
            if (key === 'propertyId' || key === 'scheduleId') {
                newFilters.userId = '';
            }
            return newFilters;
        });
        setLocalEdits({});
        setSelectedIds(new Set());
    };

    const handleWeekChange = (start, end) => {
        setFilters(prev => ({ ...prev, startDate: start, endDate: end, page: 1 }));
        setLocalEdits({});
        setSelectedIds(new Set());
    };

    const refreshAttendance = () => {
        dispatch(AttendenceManagementAction.GetAttendence(filters));
    };

    // ── Approval handler ──────────────────────────────────────
    const handleApprove = () => {
        if (selectedIds.size === 0) {
            Utils.showMessage('warning', 'Please select at least one attendance record to approve.');
            return;
        }
        // Validate: all-or-nothing — refuse if any selected row is already approved/locked/non-editable
        const selectedRecords = (attendenceData || []).filter((rec) => {
            const key = rec.shiftUserId || rec.id;
            return selectedIds.has(key);
        });
        const invalid = selectedRecords.find(
            (rec) =>
                isRecordApproved(rec) ||
                rec.isLocked === true ||
                rec.isEditable === false
        );
        if (invalid) {
            Utils.showMessage(
                'error',
                'One or more selected records are already approved, locked, or not editable. No records were approved.'
            );
            return;
        }
        dispatch(
            AttendenceManagementAction.ApproveAttendance({
                shiftUserIds: Array.from(selectedIds),
            })
        );
        setSelectedIds(new Set());
    };

    // ── Row selection toggle ───────────────────────────────────
    const toggleRowSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // ── Approve button disabled logic ─────────────────────────
    const selectedRecordsForBtn = (attendenceData || []).filter((rec) => {
        const key = rec.shiftUserId || rec.id;
        return selectedIds.has(key);
    });
    const approveDisabled =
        approving ||
        selectedIds.size === 0 ||
        selectedRecordsForBtn.some(
            (rec) =>
                isRecordApproved(rec) ||
                rec.isLocked === true ||
                rec.isEditable === false
        );

    // Which local calendar day an inline-edited time actually belongs to. The
    // row's date is only a fallback: an existing value keeps its own local
    // date, and a check-out picked earlier than the check-in rolls over to the
    // next day (overnight shift), so it can never be saved before the check-in.
    const resolveSaveDate = (rowDate, field, picked, ctx = {}) => {
        const ownDate = localDateOf(field === 'in' ? ctx.effectiveIn : ctx.effectiveOut);
        if (field === 'in') return ownDate || rowDate;

        const inDate = localDateOf(ctx.effectiveIn);
        const inLocal = utcToLocal(ctx.effectiveIn);
        if (!inDate || !inLocal || !inLocal.isValid()) return ownDate || rowDate;

        const pickedMins = picked.hour() * 60 + picked.minute();
        const inMins = inLocal.hour() * 60 + inLocal.minute();
        return pickedMins < inMins
            ? dayjs(`${inDate}T12:00:00`).add(1, 'day').format('YYYY-MM-DD')
            : inDate;
    };

    // inline In/Out save.
    // `date` is the row (shift) day — used only as the localEdits key so the
    // optimistic value lands back on the row the user clicked. `saveDate` is
    // the day the value is actually stored on; for an overnight check-out
    // those two differ (row = 7 Aug, saveDate = 8 Aug).
    const handleInlineSave = async (date, field, time, rec, saveDate) => {
        if (!time) { setEditingCell(null); return; }
        const targetDate = saveDate || date;
        // `time` is the local wall-clock value picked by the user; convert to
        // UTC (auto-detected timezone, DST-safe) before it goes anywhere near the API.
        const localDateTime = dayjs(`${targetDate} ${time.format('HH:mm')}`, 'YYYY-MM-DD HH:mm');
        const datetime = localToUtc(localDateTime);
        if (!datetime) {
            Utils.showMessage('error', 'Invalid date/time selected');
            setEditingCell(null);
            return;
        }
        const displayStr = time.format(is24h ? 'HH:mm' : 'hh:mm A');
        const localKey = `${date}|${field}`;

        // optimistic: show the new time immediately, keep UTC datetime for calculations
        setLocalEdits((prev) => ({ ...prev, [localKey]: { display: displayStr, datetime } }));
        setEditingCell(null);

        const recId = rec?.shift_user_id || rec?.shiftUserId || rec?.id;
        if (!recId && !filters.userId) {
            Utils.showMessage('error', 'Cannot save: no user selected');
            setLocalEdits((prev) => { const n = { ...prev }; delete n[localKey]; return n; });
            return;
        }

        setSavingCell({ date, field });
        try {
            if (recId) {
                // existing record for this day — patch just the changed field
                const payload = { shiftUserId: recId };
                if (field === 'in') payload.checkIn = datetime;
                else payload.checkOut = datetime;
                await ApiCaller.Patch('/attendance', payload);
            } else {
                // no record for this day yet — create one
                const payload = { userId: filters.userId, date };
                if (field === 'in') payload.checkIn = datetime;
                else payload.checkOut = datetime;
                await ApiCaller.Post('/attendance', payload);
            }
            Utils.showMessage(
                'success',
                targetDate === date
                    ? 'Time updated'
                    : `Time updated — saved on ${dayjs(targetDate).format(dateFormat)} (next day)`
            );
            refreshAttendance();
        } catch (e) {
            // revert optimistic update on failure
            setLocalEdits((prev) => { const n = { ...prev }; delete n[localKey]; return n; }); // eslint-disable-line no-unused-expressions
            Utils.showMessage('error', e?.response?.data?.message || 'Failed to update');
        } finally {
            setSavingCell(null);
        }
    };

    // Another Entry save
    const handleAnotherEntrySave = async () => {
        if (!anotherEntryModal.checkIn || !anotherEntryModal.userId) return;
        // Times picked in the modal are local wall-clock; convert to UTC before sending.
        const inLocal = dayjs(`${anotherEntryModal.date} ${anotherEntryModal.checkIn.format('HH:mm')}`, 'YYYY-MM-DD HH:mm');
        let outLocal = anotherEntryModal.checkOut
            ? dayjs(`${anotherEntryModal.date} ${anotherEntryModal.checkOut.format('HH:mm')}`, 'YYYY-MM-DD HH:mm')
            : null;
        // A check-out at or before the check-in means the shift ran past midnight,
        // so it belongs to the next calendar day (9 PM → 1 AM = +1 day).
        if (outLocal && !outLocal.isAfter(inLocal)) outLocal = outLocal.add(1, 'day');

        const checkIn = localToUtc(inLocal);
        const checkOut = outLocal ? localToUtc(outLocal) : null;
        if (!checkIn) {
            Utils.showMessage('error', 'Invalid check-in time');
            return;
        }
        try {
            await ApiCaller.Post('/attendance', {
                userId: anotherEntryModal.userId,
                date: anotherEntryModal.date,
                checkIn,
                ...(checkOut ? { checkOut } : {}),
            });
            Utils.showMessage('success', 'Entry added');
            setAnotherEntryModal({ open: false, date: null, checkIn: null, checkOut: null, userId: null });
            refreshAttendance();
        } catch (e) {
            Utils.showMessage('error', e?.response?.data?.message || 'Failed to add entry');
        }
    };

    // Time Off save
    const handleTimeOffSave = async () => {
        if (!timeOffModal.userId) return;
        try {
            await ApiCaller.Post('/attendance/time-off', {
                userId: timeOffModal.userId,
                date: timeOffModal.date,
                note: timeOffModal.note,
            });
            Utils.showMessage('success', 'Time off recorded');
            setTimeOffModal({ open: false, date: null, note: '', userId: null });
            refreshAttendance();
        } catch (e) {
            Utils.showMessage('error', e?.response?.data?.message || 'Failed to record time off');
        }
    };

    // Notes save
    const handleNotesSave = async () => {
        if (!notesModal.record) return;
        try {
            await ApiCaller.Patch('/attendance', {
                shiftUserId: notesModal.record.shiftUserId,
                notes: notesModal.text,
            });
            Utils.showMessage('success', 'Notes saved');
            setNotesModal({ open: false, record: null, text: '' });
            refreshAttendance();
        } catch (e) {
            Utils.showMessage('error', e?.response?.data?.message || 'Failed to save notes');
        }
    };

    // Break save
    const handleBreakSave = async () => {
        if (!breakModal.record || !breakModal.start) return;
        const startStr = `${breakModal.record?.shift?.shiftDate || payPeriod.start} ${breakModal.start.format('HH:mm')}`;
        const endStr = breakModal.end
            ? `${breakModal.record?.shift?.shiftDate || payPeriod.start} ${breakModal.end.format('HH:mm')}`
            : null;
        try {
            await ApiCaller.Post('/attendance/break', {
                shiftUserId: breakModal.record.shiftUserId,
                breakType: breakModal.breakType,
                start: startStr,
                ...(endStr ? { end: endStr } : {}),
            });
            Utils.showMessage('success', `${breakModal.breakType === 'tea' ? 'Tea break' : 'Lunch break'} recorded`);
            setBreakModal({ open: false, record: null, breakType: null, start: null, end: null });
            refreshAttendance();
        } catch (e) {
            Utils.showMessage('error', e?.response?.data?.message || 'Failed to record break');
        }
    };

    // Event history
    const handleOpenHistory = async (rec) => {
        setHistoryModal({ open: true, record: rec, history: [], loading: true });
        try {
            const res = await ApiCaller.Get(`/attendance/${rec.shiftUserId}/history`);
            let histData = res?.data?.data;
            if (histData && Array.isArray(histData.items)) histData = histData.items;
            const list = Array.isArray(histData) ? histData : [];
            // API guarantees newest-first; sort defensively in case that ever drifts.
            list.sort((a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0));
            setHistoryModal((prev) => ({ ...prev, history: list, loading: false }));
        } catch {
            // show empty if endpoint not ready
            setHistoryModal((prev) => ({ ...prev, loading: false }));
        }
    };

    // ── derived ───────────────────────────────────────────────
    // Frontend search over the full /users/all list — matches firstName,
    // lastName, or the two together so "john d" finds "John Doe".
    const userQuery = userSearch.trim().toLowerCase();
    const filteredUsers = !userQuery
        ? (sidebarUsersData || [])
        : (sidebarUsersData || []).filter((u) => {
            const first = (u.firstName || '').toLowerCase();
            const last = (u.lastName || '').toLowerCase();
            return first.includes(userQuery)
                || last.includes(userQuery)
                || `${first} ${last}`.trim().includes(userQuery);
        });

    const allSelectableIds = (attendenceData || []).filter(rec => {
        const isApproved = isRecordApproved(rec);
        const isRowLocked = rec?.isLocked === true;
        const isNotEditable = rec?.isEditable === false;
        return !!(rec.shiftUserId || rec.id) && !isApproved && !isRowLocked && !isNotEditable;
    }).map(rec => rec.shiftUserId || rec.id);

    const toggleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(new Set(allSelectableIds));
        } else {
            setSelectedIds(new Set());
        }
    };
    // Recalculate totals from checkIn/checkOut (respects localEdits)
    const SCHEDULED_DEFAULT_MIN = 480; // 8 hours
    let totalWorked = 0, totalScheduled = 0, totalDiff = 0;

    (attendenceData || []).forEach((rec) => {
        const date = rec.shift?.shiftDate || rec.date;
        const effectiveIn = localEdits[`${date}|in`]?.datetime || rec.checkIn;
        const effectiveOut = localEdits[`${date}|out`]?.datetime || rec.checkOut;

        const wMin = calcWorkedMinutes(effectiveIn, effectiveOut); // in hours
        totalWorked += wMin || 0;

        const schedMin = rec.requiredMinutes != null ? rec.requiredMinutes : SCHEDULED_DEFAULT_MIN; // in minutes
        totalScheduled += (schedMin / 60);

        if (wMin != null && schedMin != null) {
            totalDiff += (wMin || 0) - (schedMin / 60);
        }
    });
    console.log(totalDiff, attendenceData, "This is total diff");


    // ── cell renderers ────────────────────────────────────────
    const renderTimeCell = (date, field, rec, ctx = {}) => {
        const isEditing = editingCell?.date === date && editingCell?.field === field;
        const isSaving = savingCell?.date === date && savingCell?.field === field;
        const value = field === 'in' ? rec?.checkIn : rec?.checkOut;
        const localKey = `${date}|${field}`;
        const display = localEdits[localKey]?.display || (value ? formatTS(value, timeFormat) : (rec ? '-' : ''));
        const isApproved = isRecordApproved(rec);
        // only editable when a shift/record exists for this day
        const canEdit = rec && !isApproved;

        // UTC value currently in effect for this cell (an optimistic local edit wins)
        const effective = field === 'in' ? ctx.effectiveIn : ctx.effectiveOut;
        // > 0 only for a check-out that lands on a later local day than the check-in
        const offset = field === 'out' ? (ctx.outOffset || 0) : 0;

        if (isSaving) return <Circular spinning size={14} color={colorPrimary} />;

        if (isEditing) {
            // An overnight check-out owns a different date than the row, so it
            // needs a full date+time picker — a time-only picker would silently
            // re-save 1 AM back onto the check-in's day.
            if (offset > 0) {
                return (
                    <DatePicker
                        autoFocus
                        open
                        size="small"
                        style={{ width: 176 }}
                        showTime={{ format: pickerFmt, use12Hours: !is24h }}
                        format={`${dateFormat} ${pickerFmt}`}
                        defaultValue={utcToLocal(effective) || undefined}
                        onOk={(val) => {
                            if (val) handleInlineSave(date, field, val, rec, val.format('YYYY-MM-DD'));
                        }}
                        onChange={(val) => { if (!val) setEditingCell(null); }}
                        onOpenChange={(open) => { if (!open) setEditingCell(null); }}
                    />
                );
            }
            return (
                <TimePicker
                    autoFocus
                    open
                    size="small"
                    style={{ width: 82 }}
                    use12Hours={!is24h}
                    format={pickerFmt}
                    defaultValue={value ? utcToLocal(value) : undefined}
                    onChange={(time) => {
                        if (time) handleInlineSave(date, field, time, rec, resolveSaveDate(date, field, time, ctx));
                        else setEditingCell(null);
                    }}
                    onOpenChange={(open) => { if (!open) setEditingCell(null); }}
                />
            );
        }

        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Tooltip title={isApproved ? "Approved records cannot be edited" : "Click to edit"}>
                    <span
                        onClick={() => canEdit && setEditingCell({ date, field })}
                        style={{
                            cursor: canEdit ? 'pointer' : 'default',
                            borderBottom: canEdit ? `1px dashed ${colorPrimary}` : 'none',
                            padding: '1px 2px',
                            fontSize: 12,
                            color: '#333',
                            display: 'inline-block',
                            minWidth: 40,
                        }}
                    >
                        {display}
                    </span>
                </Tooltip>
                {offset > 0 && (
                    <OvernightInfo
                        checkInUtc={ctx.effectiveIn}
                        checkOutUtc={ctx.effectiveOut}
                        offset={offset}
                        dateFormat={dateFormat}
                        timeFormat={timeFormat}
                    />
                )}
            </span>
        );
    };

    // ── render ────────────────────────────────────────────────
    return (
        <div style={{ display: 'flex', height: 'calc(100vh - 80px)', background: '#f5f6fa' }}>

            {/* ======== SIDEBAR ======== */}
            <div style={{
                width: 240, minWidth: 240, background: '#fff',
                borderRight: '1px solid #e8ecf0',
                display: 'flex', flexDirection: 'column', padding: '16px 14px', gap: 10,
                overflow: 'hidden',
                maxHeight: 'calc(100vh - 80px)'
            }}>
                {/* Pay Period - week only */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#222' }}>Pay Period</span>
                    </div>
                    <WeekSelector
                        start={workWeek.startDate || filters.startDate}
                        end={workWeek.endDate || filters.endDate}
                        weekStartDay={workWeek.startDay}
                        onChange={handleWeekChange}
                    />
                </div>

                {/* Close Period */}
                <button style={{
                    border: '1px solid #d9d9d9', background: '#fff', borderRadius: 4,
                    padding: '6px 12px', cursor: 'pointer', fontSize: 11,
                    fontWeight: 700, color: '#444', letterSpacing: 0.8, textAlign: 'center',
                }}>
                    CLOSE PERIOD
                </button>

                {/* Schedule filter */}
                <div>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#666', display: 'block', marginBottom: 4 }}>Schedule</span>
                    <ConfigProvider theme={{ components: { Select: { optionSelectedBg: colorPrimary, optionActiveBg: Colors.LightGray, optionSelectedColor: Colors.White } } }}>
                        <Select
                            className="w-full" size="small"
                            value={filters.scheduleId || undefined}
                            placeholder="All Schedules"
                            allowClear onChange={(val) => handleFilterChange('scheduleId', val || '')}
                            options={schedulesData || []}
                        />
                    </ConfigProvider>
                </div>

                {/* Property filter */}
                <div>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#666', display: 'block', marginBottom: 4 }}>Property</span>
                    <ConfigProvider theme={{ components: { Select: { optionSelectedBg: colorPrimary, optionActiveBg: Colors.LightGray, optionSelectedColor: Colors.White } } }}>
                        <PaginatedSelect
                            className="w-full" size="small"
                            fetchPage={fetchPropertiesPage}
                            showFullLabelToggle
                            extraOptions={selectedPropOption ? [selectedPropOption] : []}
                            value={filters.propertyId || undefined}
                            placeholder="Search properties"
                            allowClear
                            onChange={(val, option) => {
                                setSelectedPropOption(val ? option : null);
                                handleFilterChange('propertyId', val || '');
                            }}
                        />
                    </ConfigProvider>
                </div>

                {/* Timesheets + Export */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#222' }}>Timesheets</span>
                    <button
                        onClick={() => attendenceData?.length > 0 && convertToPdf(printRef)}
                        style={{
                            background: '#f5f6fa', border: '1px solid #d9d9d9', borderRadius: 4,
                            padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                            fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, color: '#444',
                        }}>
                        <DownloadOutlined style={{ fontSize: 11 }} />EXPORT
                    </button>
                </div>

                {/* Search */}
                <Input size="small" placeholder="Seraching Users..." value={userSearch}
                    allowClear
                    onChange={(e) => setUserSearch(e.target.value)}
                    style={{ borderRadius: 4 }} />

                {/* User list — /users/all is unpaginated, so this just scrolls */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                    {filteredUsers.map((u) => (
                        <div key={u.value} onClick={() => handleFilterChange('userId', filters.userId === u.value ? '' : u.value)} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '7px 8px', cursor: 'pointer', borderRadius: 4, marginBottom: 2,
                            borderLeft: filters.userId === u.value ? `3px solid ${colorPrimary}` : '3px solid transparent',
                            background: filters.userId === u.value ? '#e6f4ff' : 'transparent',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}>
                            {u.profileImg
                                ? <Avatar src={u.profileImg} size={28} />
                                : <Avatar icon={<UserOutlined />} size={28} style={{ background: '#e0e0e0', color: '#888' }} />}
                            <span style={{ fontSize: 13, color: '#333', flex: 1 }}>{u.label}</span>
                            {u.hasWarning && <Tooltip title="Missing check-out"><span style={{ color: '#faad14' }}>⚠</span></Tooltip>}
                        </div>
                    ))}
                    {sidebarUsersLoader && (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                            <Circular spinning size={22} color={colorPrimary} />
                        </div>
                    )}
                    {!sidebarUsersLoader && filteredUsers.length === 0 && (
                        <p style={{ fontSize: 12, color: '#aaa', textAlign: 'center', marginTop: 20 }}>
                            {userQuery
                                ? <>No users matched “{userSearch.trim()}”</>
                                : 'No users found'}
                        </p>
                    )}
                </div>
            </div>

            {/* ======== MAIN CONTENT ======== */}
            <div style={{ flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <>
                    {/* ── Header ── */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexShrink: 0 }}>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                            {filters.userId ? sidebarUsersData?.find(u => u.value === filters.userId)?.label : 'All Users Attendance'}
                        </h2>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Tooltip title={approveDisabled && selectedIds.size > 0 ? 'Selected records cannot be approved (already approved, locked, or not editable)' : selectedIds.size === 0 ? 'Select records to approve' : ''}>
                                <button
                                    id="attendance-approve-btn"
                                    onClick={handleApprove}
                                    disabled={approveDisabled}
                                    style={{
                                        background: approveDisabled ? '#d9d9d9' : colorPrimary,
                                        color: approveDisabled ? '#999' : '#fff',
                                        border: 'none', borderRadius: 4,
                                        padding: '7px 20px', fontWeight: 700, fontSize: 12,
                                        cursor: approveDisabled ? 'not-allowed' : 'pointer',
                                        letterSpacing: 0.8,
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        transition: 'background 0.2s',
                                    }}
                                >
                                    {approving && <Circular spinning size={12} color="#fff" />}
                                    APPROVE{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                                </button>
                            </Tooltip>
                            {/* <button style={{ background: '#fff', color: '#333', border: '1px solid #d9d9d9', borderRadius: 4, padding: '7px 16px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                SHOW SHIFTS
                            </button> */}
                            <button
                                onClick={() => attendenceData?.length > 0 && convertToPdf(printRef)}
                                style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, padding: '7px 10px', cursor: 'pointer', lineHeight: 1 }}
                                title="Print">
                                <PrinterOutlined style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    </div>

                    {/* ── Summary stats ── */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 13, paddingBottom: 10, borderBottom: '1px solid #eef0f3', marginBottom: 12, flexShrink: 0 }}>
                        {[
                            { label: 'Regular', val: summary.regular },
                            { label: 'Overtime', val: summary.overTime },
                            { label: 'Double OT', val: summary.doubleOT },
                            { label: 'Time Off', val: summary.timeOff },
                            { label: 'Paid Total', val: summary.paidTotal },
                            { label: 'Scheduled vs. Worked', val: formatDiffHM(totalDiff) },
                        ].map((s, i, arr) => (
                            <React.Fragment key={s.label}>
                                <span>
                                    <span style={{ color: '#1890ff', fontWeight: 500 }}>{s.label} </span>
                                    <span style={{ fontWeight: 700 }}>{s.val ?? 0}</span>
                                </span>
                                {i < arr.length - 1 && <span style={{ color: '#d9d9d9', userSelect: 'none' }}>|</span>}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* ── TABLE AREA ── */}
                    {loader ? (
                        <Circular spinning size={36} color={colorPrimary}
                            containerStyle={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        />
                    ) : !filters.userId ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', background: '#fcfcfc', border: '1px solid #e0e4e8', borderRadius: 8 }}>
                            <h3 style={{ fontWeight: 500, color: '#666' }}>Please select a user from the sidebar to view their attendance.</h3>
                        </div>
                    ) : (
                        <div
                            ref={contentRef}
                            style={{
                                flex: 1, display: 'flex', flexDirection: 'column',
                                border: '1px solid #e0e4e8', borderRadius: 8,
                                overflow: 'hidden', minHeight: 0,
                            }}
                        >
                            {/* ─ Scrollable body (header lives inside so horizontal scroll stays in sync) ─ */}
                            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                                {/* ─ Header row – sticky vertically, scrolls horizontally with body ─ */}
                                <div style={{ ...rowStyle, background: '#f0f2f5', borderBottom: '2px solid #d9dde3', position: 'sticky', top: 0, zIndex: 2 }}>
                                    <div style={{ ...cellStyle, width: 32, flexShrink: 0, justifyContent: 'center' }}>
                                        {allSelectableIds.length > 0 && (
                                            <input
                                                type="checkbox"
                                                checked={allSelectableIds.length > 0 && allSelectableIds.every(id => selectedIds.has(id))}
                                                onChange={toggleSelectAll}
                                                style={{ cursor: 'pointer', width: 14, height: 14, accentColor: colorPrimary }}
                                                title="Select All"
                                            />
                                        )}
                                    </div>
                                    <div style={{ ...cellStyle, width: CW.day, fontWeight: 700 }}>Day</div>
                                    <div style={{ ...cellStyle, width: CW.in, textAlign: 'center', fontWeight: 700 }}>Check-In</div>
                                    <div style={{ ...cellStyle, width: CW.out, textAlign: 'center', fontWeight: 700 }}>Check-Out</div>
                                    <div style={{ ...cellStyle, width: CW.total, textAlign: 'center', fontWeight: 700 }}>Total</div>
                                    <div style={{ ...cellStyle, width: CW.details, textAlign: 'center', fontWeight: 700 }}>Details</div>
                                    <div style={{ ...cellStyle, width: CW.worked, textAlign: 'center', fontWeight: 700 }}>Worked</div>
                                    <div style={{ ...cellStyle, width: CW.scheduled, textAlign: 'center', fontWeight: 700 }}>Scheduled</div>
                                    <div style={{ ...cellStyle, flex: 1, textAlign: 'center', fontWeight: 700, borderRight: 'none' }}>Difference</div>
                                </div>
                                {generateWeekDays(workWeek.startDate, workWeek.endDate).map((date, idx) => {
                                    const dayRecords = (attendenceData || []).filter(r => {
                                        const rDate = r.shift?.shiftDate || r.date || (r.checkIn ? utcToLocal(r.checkIn)?.format('YYYY-MM-DD') : '');
                                        return rDate === date;
                                    });
                                    const recordsToRender = dayRecords.length > 0 ? dayRecords : [null];

                                    return recordsToRender.map((rec, rIdx) => {
                                        const dayObj = dayjs(date);
                                        const effectiveIn = localEdits[`${date}|in`]?.datetime || rec?.checkIn;
                                        const effectiveOut = localEdits[`${date}|out`]?.datetime || rec?.checkOut;
                                        const workedMin = rec ? calcWorkedMinutes(effectiveIn, effectiveOut) : null;
                                        const scheduledMin = rec ? (rec.requiredMinutes ?? SCHEDULED_DEFAULT_MIN) : null;

                                        // Overnight shift: the check-out lands on a later local day than the
                                        // check-in. The row stays on the check-in's day; the cell flags the shift.
                                        const cellCtx = {
                                            effectiveIn,
                                            effectiveOut,
                                            outOffset: checkOutDayOffset(effectiveIn, effectiveOut),
                                        };

                                        let diffValue = null;
                                        if (workedMin != null && scheduledMin != null) {
                                            diffValue = workedMin - (scheduledMin / 60);
                                        }

                                        const diffColor = diffValue != null && diffValue < 0 ? '#f5222d' : '#389e0d';

                                        const rowKey = rec ? (rec.shiftUserId || rec.id || `${date}-${rIdx}`) : `${date}-empty`;
                                        const isHoveredRow = hoveredRow === rowKey;
                                        const isHoveredDay = hoveredDayRow === rowKey;
                                        const isMissingOut = rec && rec.checkIn && !rec.checkOut;

                                        const recKey = rec ? (rec.shiftUserId || rec.id) : null;
                                        const isApproved = isRecordApproved(rec);
                                        const isRowLocked = rec?.isLocked === true;
                                        const isNotEditable = rec?.isEditable === false;
                                        const canSelect = !!recKey && !isApproved && !isRowLocked && !isNotEditable;
                                        const isSelected = recKey && selectedIds.has(recKey);

                                        return (
                                            <div
                                                key={rowKey}
                                                style={{
                                                    ...rowStyle,
                                                    background: isSelected
                                                        ? '#e6f4ff'
                                                        : idx % 2 === 0 ? '#fff' : '#fafbfd',
                                                    minHeight: 48,
                                                    outline: isSelected ? `1px solid #91caff` : 'none',
                                                }}
                                                onMouseEnter={() => setHoveredRow(rowKey)}
                                                onMouseLeave={() => setHoveredRow(null)}
                                            >
                                                {/* Selection checkbox / approved indicator */}
                                                <div style={{ ...cellStyle, width: 32, flexShrink: 0, justifyContent: 'center', borderBottom: rIdx === recordsToRender.length - 1 ? '1px solid #eaecef' : 'none' }}>
                                                    {rec && (
                                                        isApproved ? (
                                                            <Tooltip title="Approved">
                                                                <CheckSquareOutlined style={{ fontSize: 16, color: '#52c41a' }} />
                                                            </Tooltip>
                                                        ) : isRowLocked ? (
                                                            <Tooltip title="Record is locked">
                                                                <span style={{ fontSize: 14, color: '#faad14' }}>🔒</span>
                                                            </Tooltip>
                                                        ) : isNotEditable ? (
                                                            <Tooltip title="Record is not editable">
                                                                <span style={{ fontSize: 13, color: '#bbb' }}>—</span>
                                                            </Tooltip>
                                                        ) : (
                                                            <input
                                                                type="checkbox"
                                                                checked={!!isSelected}
                                                                onChange={() => toggleRowSelect(recKey)}
                                                                style={{ cursor: 'pointer', width: 14, height: 14, accentColor: colorPrimary }}
                                                                id={`chk-${rowKey}`}
                                                            />
                                                        )
                                                    )}
                                                </div>

                                                {/* Day cell */}
                                                <div
                                                    style={{
                                                        ...cellStyle,
                                                        width: CW.day,
                                                        position: 'relative',
                                                        paddingRight: 28,
                                                        borderBottom: rIdx === recordsToRender.length - 1 ? '1px solid #eaecef' : 'none'
                                                    }}
                                                    onMouseEnter={() => setHoveredDayRow(rowKey)}
                                                    onMouseLeave={() => setHoveredDayRow(null)}
                                                >
                                                    {rIdx === 0 && (
                                                        <>
                                                            <div>
                                                                <span style={{ color: rec ? '#1890ff' : '#666', fontWeight: 500, fontSize: 12 }}>
                                                                    {/* {rec.user ? `${rec.user.firstName || ''} ${rec.user.lastName || ''}`.trim() : 'Unknown User'} */}
                                                                    <br />
                                                                    {dayObj.isValid() ? dayObj.format('ddd, MMM D') : date}
                                                                </span>
                                                                {rec?.checkIn && (
                                                                    <div style={{ color: '#999', fontSize: 10, marginTop: 1 }}>
                                                                        {formatTS(rec.checkIn, timeFormat)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {/* + icon: visible on hover OR while its popover is open */}
                                                            {(isHoveredDay || popoverDate === rowKey) && (
                                                                <Popover
                                                                    open={popoverDate === rowKey}
                                                                    onOpenChange={(visible) => setPopoverDate(visible ? rowKey : null)}
                                                                    trigger="click"
                                                                    placement="rightTop"
                                                                    content={
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 140 }}>
                                                                            <button
                                                                                style={popBtnStyle}
                                                                                onClick={() => {
                                                                                    setPopoverDate(null);
                                                                                    setAnotherEntryModal({ open: true, date, checkIn: null, checkOut: null, userId: rec?.user?.id || filters.userId });
                                                                                }}
                                                                            >
                                                                                + Another Entry
                                                                            </button>
                                                                            <button
                                                                                style={popBtnStyle}
                                                                                onClick={() => {
                                                                                    setPopoverDate(null);
                                                                                    setTimeOffModal({ open: true, date, note: '', userId: rec?.user?.id || filters.userId });
                                                                                }}
                                                                            >
                                                                                🏖 Time Off
                                                                            </button>
                                                                        </div>
                                                                    }
                                                                >
                                                                    <span
                                                                        style={{
                                                                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                                                                            width: 18, height: 18, borderRadius: '50%',
                                                                            background: colorPrimary, color: '#fff',
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            cursor: 'pointer', fontSize: 12, lineHeight: 1,
                                                                        }}
                                                                    >
                                                                        <PlusOutlined style={{ fontSize: 9 }} />
                                                                    </span>
                                                                </Popover>
                                                            )}
                                                        </>
                                                    )}
                                                </div>

                                                {/* In */}
                                                <div style={{ ...cellStyle, width: CW.in, textAlign: 'center', justifyContent: 'center', borderBottom: '1px solid #eaecef' }}>
                                                    {renderTimeCell(date, 'in', rec, cellCtx)}
                                                </div>

                                                {/* Out */}
                                                <div style={{ ...cellStyle, width: CW.out, textAlign: 'center', justifyContent: 'center', borderBottom: '1px solid #eaecef' }}>
                                                    {renderTimeCell(date, 'out', rec, cellCtx)}
                                                </div>

                                                {/* Total = checkOut − checkIn */}
                                                <div style={{ ...cellStyle, width: CW.total, textAlign: 'center', justifyContent: 'center', fontWeight: 600, borderBottom: '1px solid #eaecef' }}>
                                                    {workedMin}
                                                </div>

                                                {/* Details */}
                                                <div style={{ ...cellStyle, width: CW.details, flexWrap: 'wrap', gap: 4, justifyContent: 'flex-start', position: 'relative', borderBottom: '1px solid #eaecef' }}>
                                                    {rec && (
                                                        <>
                                                            {rec.shift?.property?.schedules?.[0]?.name && (
                                                                <DetailBadge icon="📅" label={rec.shift.property.schedules[0].name} />
                                                            )}
                                                            {rec.shift?.eligibilitity?.name && (
                                                                <DetailBadge icon="👤" label={rec.shift.eligibilitity.name} />
                                                            )}
                                                            {rec.shift?.property?.name && (
                                                                <DetailBadge icon="📍" label={rec.shift.property.name} />
                                                            )}
                                                        </>
                                                    )}
                                                    {/* 4 hover icons – shown when row is hovered */}
                                                    {rec && isHoveredRow && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                                                            {/* ① Info – event history */}
                                                            <Tooltip title="Event history">
                                                                <InfoCircleOutlined
                                                                    style={iconStyle(colorPrimary)}
                                                                    onClick={() => handleOpenHistory(rec)}
                                                                />
                                                            </Tooltip>
                                                            {/* ② Tea break */}
                                                            <Tooltip title="Tea break">
                                                                <CoffeeOutlined
                                                                    style={iconStyle('#722ed1')}
                                                                    onClick={() => setBreakModal({ open: true, record: rec, breakType: 'tea', start: null, end: null })}
                                                                />
                                                            </Tooltip>
                                                            {/* ③ Lunch break */}
                                                            <Tooltip title="Lunch break">
                                                                <span
                                                                    style={{ ...iconStyle('#d46b08'), lineHeight: 0, display: 'inline-flex', alignItems: 'center' }}
                                                                    onClick={() => setBreakModal({ open: true, record: rec, breakType: 'lunch', start: null, end: null })}
                                                                >
                                                                    <MdLunchDining size={16} />
                                                                </span>
                                                            </Tooltip>
                                                            {/* ④ Notes */}
                                                            <Tooltip title="Add notes">
                                                                <FileTextOutlined
                                                                    style={iconStyle('#389e0d')}
                                                                    onClick={() => setNotesModal({ open: true, record: rec, text: rec.notes || '' })}
                                                                />
                                                            </Tooltip>
                                                            {/* Manual clock actions */}
                                                            {canManualClock && isMissingOut && (
                                                                <Tooltip title="Manual Clock Out">
                                                                    <span
                                                                        style={{ fontSize: 10, background: '#fff7e6', border: '1px solid #ffc53d', borderRadius: 4, padding: '1px 5px', color: '#d46b08', fontWeight: 700, cursor: 'pointer' }}
                                                                        onClick={() => { setEditTimeData({ id: rec.shiftUserId, checkIn: rec.checkIn, checkOut: '', date }); setDrawerStates({ checkoutTimeDrawer: true }); }}
                                                                    >
                                                                        Clock Out
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Manual Clock In for days with shift but no check-in */}
                                                    {rec && !rec.checkIn && canManualClock && (
                                                        <Tooltip title="Manual Clock In">
                                                            <span
                                                                style={{ fontSize: 10, background: '#f6ffed', border: '1px solid #73d13d', borderRadius: 4, padding: '1px 5px', color: '#389e0d', fontWeight: 700, cursor: 'pointer' }}
                                                                onClick={() => { setEditTimeData({ id: rec.shiftUserId, checkIn: '', checkOut: '', date }); setDrawerStates({ checkoutTimeDrawer: true }); }}
                                                            >
                                                                Clock In
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </div>

                                                {/* Worked = same as Total */}
                                                <div style={{ ...cellStyle, width: CW.worked, textAlign: 'center', justifyContent: 'center', borderBottom: '1px solid #eaecef' }}>
                                                    {workedMin}
                                                </div>

                                                {/* Scheduled */}
                                                <div style={{ ...cellStyle, width: CW.scheduled, textAlign: 'center', justifyContent: 'center', borderBottom: '1px solid #eaecef' }}>
                                                    {scheduledMin != null ? toDecimalHours(scheduledMin) : ''}
                                                </div>

                                                {/* Difference = Worked − Scheduled (h m format) */}
                                                <div style={{ ...cellStyle, flex: 1, textAlign: 'center', justifyContent: 'center', fontWeight: 600, color: diffColor, borderRight: 'none', borderBottom: '1px solid #eaecef' }}>
                                                    {formatDiffHM(diffValue)}
                                                </div>
                                            </div>
                                        );
                                    });
                                })}

                                {/* ─ Total row – scrolls with the table ─ */}
                                <div style={{ ...rowStyle, background: '#f0f2f5', borderTop: '2px solid #d9dde3' }}>
                                    <div style={{ ...cellStyle, width: 32, flexShrink: 0 }} />
                                    <div style={{ ...cellStyle, width: CW.day, fontWeight: 700 }}>Total</div>
                                    <div style={{ ...cellStyle, width: CW.in }} />
                                    <div style={{ ...cellStyle, width: CW.out }} />
                                    <div style={{ ...cellStyle, width: CW.total, textAlign: 'center', justifyContent: 'center', fontWeight: 700 }}>
                                        {totalWorked}
                                    </div>
                                    <div style={{ ...cellStyle, width: CW.details }} />
                                    <div style={{ ...cellStyle, width: CW.worked, textAlign: 'center', justifyContent: 'center', fontWeight: 700 }}>
                                        {totalWorked.toFixed(2)}
                                    </div>
                                    <div style={{ ...cellStyle, width: CW.scheduled, textAlign: 'center', justifyContent: 'center', fontWeight: 700 }}>
                                        {totalScheduled.toFixed(2)}
                                    </div>
                                    <div style={{ ...cellStyle, flex: 1, textAlign: 'center', justifyContent: 'center', fontWeight: 700, color: totalDiff < 0 ? '#f5222d' : '#389e0d', borderRight: 'none' }}>
                                        {formatDiffHM(totalDiff)}
                                    </div>
                                </div>
                            </div>
                            {/* Pagination */}
                            {/* {paginate && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', background: '#fff', borderTop: '1px solid #e0e4e8' }}>
                                    <Pagination
                                        current={paginate.currentPage || filters.page}
                                        pageSize={paginate.perPage || filters.perPage}
                                        total={paginate.total || 0}
                                        onChange={(page, pageSize) => setFilters(prev => ({ ...prev, page, perPage: pageSize }))}
                                        showSizeChanger
                                    />
                                </div>
                            )} */}
                        </div>
                    )}
                </>
                {/* )} */}
            </div>

            {/* ======== MODALS ======== */}

            {/* Another Entry */}
            <Modal
                title="Add Another Entry"
                open={anotherEntryModal.open}
                onCancel={() => setAnotherEntryModal({ open: false, date: null, checkIn: null, checkOut: null })}
                onOk={handleAnotherEntrySave}
                okText="Save Entry"
                okButtonProps={{ style: { background: colorPrimary } }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
                    <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
                        Date: <strong>{anotherEntryModal.date}</strong>
                    </p>
                    <div>
                        <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Check-In Time</label>
                        <TimePicker
                            style={{ width: '100%' }}
                            use12Hours={!is24h} format={pickerFmt}
                            onChange={(t) => setAnotherEntryModal((p) => ({ ...p, checkIn: t }))}
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>
                            Check-Out Time <span style={{ color: '#aaa' }}>(optional)</span>
                        </label>
                        <TimePicker
                            style={{ width: '100%' }}
                            use12Hours={!is24h} format={pickerFmt}
                            onChange={(t) => setAnotherEntryModal((p) => ({ ...p, checkOut: t }))}
                        />
                        {/* mirrors the roll-over applied in handleAnotherEntrySave */}
                        {anotherEntryModal.checkIn && anotherEntryModal.checkOut &&
                            !dayjs(`${anotherEntryModal.date} ${anotherEntryModal.checkOut.format('HH:mm')}`, 'YYYY-MM-DD HH:mm')
                                .isAfter(dayjs(`${anotherEntryModal.date} ${anotherEntryModal.checkIn.format('HH:mm')}`, 'YYYY-MM-DD HH:mm')) && (
                                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#d46b08' }}>
                                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                                    Overnight shift — this check-out will be saved on{' '}
                                    <strong>{dayjs(anotherEntryModal.date).add(1, 'day').format(dateFormat)}</strong> (next day).
                                </p>
                            )}
                    </div>
                </div>
            </Modal>

            {/* Time Off */}
            <Modal
                title="Record Time Off"
                open={timeOffModal.open}
                onCancel={() => setTimeOffModal({ open: false, date: null, note: '' })}
                onOk={handleTimeOffSave}
                okText="Record"
                okButtonProps={{ style: { background: colorPrimary } }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
                    <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
                        Date: <strong>{timeOffModal.date}</strong>
                    </p>
                    <div>
                        <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Note</label>
                        <Input.TextArea
                            rows={3}
                            placeholder="Reason for time off..."
                            value={timeOffModal.note}
                            onChange={(e) => setTimeOffModal((p) => ({ ...p, note: e.target.value }))}
                        />
                    </div>
                </div>
            </Modal>

            {/* Notes */}
            <Modal
                title="Add Notes"
                open={notesModal.open}
                onCancel={() => setNotesModal({ open: false, record: null, text: '' })}
                onOk={handleNotesSave}
                okText="Save Notes"
                okButtonProps={{ style: { background: colorPrimary } }}
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter notes for this attendance record..."
                    value={notesModal.text}
                    onChange={(e) => setNotesModal((p) => ({ ...p, text: e.target.value }))}
                    style={{ marginTop: 8 }}
                />
            </Modal>

            {/* Break (Tea / Lunch) */}
            <Modal
                title={breakModal.breakType === 'tea' ? '☕ Tea Break' : '🍔 Lunch Break'}
                open={breakModal.open}
                onCancel={() => setBreakModal({ open: false, record: null, breakType: null, start: null, end: null })}
                onOk={handleBreakSave}
                okText="Record Break"
                okButtonProps={{ style: { background: colorPrimary } }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
                    <div>
                        <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Break Start</label>
                        <TimePicker
                            style={{ width: '100%' }}
                            use12Hours={!is24h} format={pickerFmt}
                            onChange={(t) => setBreakModal((p) => ({ ...p, start: t }))}
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>
                            Break End <span style={{ color: '#aaa' }}>(optional)</span>
                        </label>
                        <TimePicker
                            style={{ width: '100%' }}
                            use12Hours={!is24h} format={pickerFmt}
                            onChange={(t) => setBreakModal((p) => ({ ...p, end: t }))}
                        />
                    </div>
                </div>
            </Modal>

            {/* Event History */}
            <Modal
                title="Event History"
                open={historyModal.open}
                onCancel={() => setHistoryModal({ open: false, record: null, history: [], loading: false })}
                footer={null}
                width={520}
            >
                {historyModal.loading ? (
                    <Circular spinning size={28} color={colorPrimary}
                        containerStyle={{ width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    />
                ) : (!Array.isArray(historyModal.history) || historyModal.history.length === 0) ? (
                    <p style={{ textAlign: 'center', color: '#aaa', padding: '24px 0' }}>No history available</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto' }}>
                        {historyModal.history.map((item, i) => {
                            const source = item.changeSource || item.meta?.changeSource || 'user_recorded';
                            const sourceMeta = {
                                user_recorded: { label: 'Clocked by Guard', color: '#1677ff', bg: '#e6f4ff' },
                                admin_edit: { label: 'Edited by Admin', color: '#d46b08', bg: '#fff7e6' },
                                admin_approval: { label: 'Approved by Admin', color: '#389e0d', bg: '#f6ffed' },
                            }[source] || { label: source, color: '#666', bg: '#f0f0f0' };

                            const actor = item.changedBy || item.meta?.actor;
                            const actorName = actor?.name || [actor?.firstName, actor?.lastName].filter(Boolean).join(' ');
                            const actorRoles = actor?.roles || item.meta?.actorRoles || [];

                            const prevTimes = item.attendanceTimes?.previous || item.before?.fields;
                            const newTimes = item.attendanceTimes?.updated || item.after?.fields;
                            const checkInChanged = item.fieldChanges?.checkIn?.old !== item.fieldChanges?.checkIn?.new;
                            const checkOutChanged = item.fieldChanges?.checkOut?.old !== item.fieldChanges?.checkOut?.new;

                            return (
                                <div key={item.id ?? i} style={{ padding: '10px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #eee', fontSize: 13 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ background: sourceMeta.bg, color: sourceMeta.color, padding: '2px 8px', borderRadius: 12, fontWeight: 600, fontSize: 11 }}>
                                            {sourceMeta.label}
                                        </span>
                                        <span style={{ color: '#999', fontSize: 11 }}>
                                            {item.changedAt ? formatUtcToLocalDisplay(item.changedAt, `${dateFormat} ${timeFormat}`) : ''}
                                        </span>
                                    </div>

                                    {item.description && (
                                        <div style={{ color: '#333', marginBottom: 6 }}>{item.description}</div>
                                    )}

                                    {(prevTimes || newTimes) && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
                                            {newTimes?.checkIn && (
                                                <span>
                                                    Check-In:{' '}
                                                    {checkInChanged && prevTimes?.checkIn && (
                                                        <span style={{ textDecoration: 'line-through', color: '#aaa', marginRight: 4 }}>
                                                            {formatTS(prevTimes.checkIn, timeFormat)}
                                                        </span>
                                                    )}
                                                    <span style={{ fontWeight: checkInChanged ? 600 : 400 }}>
                                                        {formatTS(newTimes.checkIn, timeFormat)}
                                                    </span>
                                                </span>
                                            )}
                                            {newTimes?.checkOut && (
                                                <span>
                                                    Check-Out:{' '}
                                                    {checkOutChanged && prevTimes?.checkOut && (
                                                        <span style={{ textDecoration: 'line-through', color: '#aaa', marginRight: 4 }}>
                                                            {formatTS(prevTimes.checkOut, timeFormat)}
                                                        </span>
                                                    )}
                                                    <span style={{ fontWeight: checkOutChanged ? 600 : 400 }}>
                                                        {formatTS(newTimes.checkOut, timeFormat)}
                                                    </span>
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {actorName && (
                                        <div style={{ color: '#666', fontSize: 12 }}>
                                            By <strong>{actorName}</strong>
                                            {actor?.email ? ` (${actor.email})` : ''}
                                            {actorRoles.length > 0 ? ` • ${actorRoles.join(', ')}` : ''}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal>

            {/* Edit Time Drawer (legacy + clock-in/out) */}
            <EditTimeDrawer
                drawerStates={drawerStates}
                setDrawerStates={setDrawerStates}
                editTimeData={editTimeData}
                filterStates={{
                    filterDrawer: {
                        user: filters.userId || '',
                        startingDate: filters.startDate,
                        endingDate: filters.endDate,
                        property: filters.propertyId || '',
                        schedule: filters.scheduleId || '',
                    },
                }}
                setFilterStates={() => { }}
                onAfterSave={refreshAttendance}
            />

            {/* ======== HIDDEN PRINT LAYOUT ======== */}
            <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
                <div ref={printRef} style={{ width: '800px', padding: '40px', background: '#fff', color: '#000', fontFamily: 'sans-serif' }}>
                    {/* Header line */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '20px' }}>
                        <span>{dayjs().format('DD/MM/YYYY, HH:mm')}</span>
                        <span>{filters.userId ? sidebarUsersData?.find(u => u.value === filters.userId)?.label : ''} {dayjs(workWeek.startDate).format('MM-DD-YYYY')} to {dayjs(workWeek.endDate).format('MM-DD-YYYY')} Timesheet</span>
                        <span style={{ visibility: 'hidden' }}>{dayjs().format('DD/MM/YYYY, HH:mm')}</span> {/* For center alignment trick */}
                    </div>

                    {/* Prominent Name */}
                    <h1 style={{ fontSize: '28px', fontWeight: 'normal', color: '#666', margin: '0 0 20px 0' }}>
                        {filters.userId ? sidebarUsersData?.find(u => u.value === filters.userId)?.label : 'All Users'}
                    </h1>

                    {/* Summary Stats */}
                    <div style={{ display: 'flex', gap: '8px', fontSize: '11px', marginBottom: '20px', color: '#555' }}>
                        <span>Regular <strong style={{ color: '#000' }}>{summary.regular ?? 0}</strong> | </span>
                        <span>Overtime <strong style={{ color: '#000' }}>{summary.overTime ?? 0}</strong> | </span>
                        <span>Double OT <strong style={{ color: '#000' }}>{summary.doubleOT ?? 0}</strong> | </span>
                        <span>Time Off <strong style={{ color: '#000' }}>{summary.timeOff ?? 0}</strong> | </span>
                        <span>Paid Total <strong style={{ color: '#000' }}>{summary.paidTotal ?? 0}</strong> | </span>
                        <span>Scheduled vs. Worked <strong style={{ color: '#000' }}>{formatDiffHM(totalDiff)}</strong></span>
                    </div>

                    {/* Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginBottom: '40px' }}>
                        <thead>
                            <tr>
                                <th style={printThStyle}>Day</th>
                                <th style={printThStyle}>Check-In</th>
                                <th style={printThStyle}>Check-Out</th>
                                <th style={printThStyle}>Total</th>
                                <th style={{ ...printThStyle, width: '30%' }}>Details</th>
                                <th style={printThStyle}>Worked</th>
                                <th style={printThStyle}>Scheduled</th>
                                <th style={printThStyle}>Difference</th>
                            </tr>
                        </thead>
                        <tbody>
                            {generateWeekDays(workWeek.startDate, workWeek.endDate).map((date) => {
                                const dayRecords = (attendenceData || []).filter(r => {
                                    // same grouping rule as the on-screen table — the fallback day
                                    // must come from the LOCAL check-in date, not the raw UTC string
                                    const rDate = r.shift?.shiftDate || r.date || localDateOf(r.checkIn) || '';
                                    return rDate === date;
                                });
                                const recordsToRender = dayRecords.length > 0 ? dayRecords : [null];

                                return recordsToRender.map((rec, rIdx) => {
                                    const dayObj = dayjs(date);
                                    const effectiveIn = localEdits[`${date}|in`]?.datetime || rec?.checkIn;
                                    const effectiveOut = localEdits[`${date}|out`]?.datetime || rec?.checkOut;
                                    const workedMin = rec ? calcWorkedMinutes(effectiveIn, effectiveOut) : null;
                                    const scheduledMin = rec ? (rec.requiredMinutes ?? SCHEDULED_DEFAULT_MIN) : null;
                                    // print has no hover/popover, so spell the overnight roll out inline
                                    const outOffset = checkOutDayOffset(effectiveIn, effectiveOut);

                                    let diffValue = null;
                                    if (workedMin != null && scheduledMin != null) {
                                        diffValue = workedMin - (scheduledMin / 60);
                                    }

                                    return (
                                        <tr key={`${date}-${rIdx}`}>
                                            <td style={printTdStyle}>
                                                {rIdx === 0 && (
                                                    <span style={{ color: '#555' }}>{dayObj.isValid() ? dayObj.format('ddd, MMM D') : date} +</span>
                                                )}
                                            </td>
                                            <td style={printTdStyle}>{rec && effectiveIn ? formatTS(effectiveIn, timeFormat) : ''}</td>
                                            <td style={printTdStyle}>
                                                {rec && effectiveOut ? formatTS(effectiveOut, timeFormat) : ''}
                                                {rec && effectiveOut && outOffset > 0 && (
                                                    <span style={{ fontSize: '9px', color: '#d46b08' }}>
                                                        {` (+${outOffset}d · ${utcToLocal(effectiveOut)?.format('MMM D')})`}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={printTdStyle}>{workedMin}</td>
                                            <td style={printTdStyle}>
                                                {rec && [
                                                    rec.shift?.property?.schedules?.[0]?.name,
                                                    rec.shift?.eligibilitity?.name,
                                                    rec.shift?.property?.name
                                                ].filter(Boolean).join(' | ')}
                                            </td>
                                            <td style={printTdStyle}>{workedMin}</td>
                                            <td style={printTdStyle}>{scheduledMin != null ? toDecimalHours(scheduledMin) : ''}</td>
                                            <td style={printTdStyle}>{formatDiffHM(diffValue)}</td>
                                        </tr>
                                    );
                                });
                            })}
                            {/* Total Row */}
                            <tr>
                                <td style={{ ...printTdStyle, fontWeight: 'bold' }}>Total</td>
                                <td style={printTdStyle}></td>
                                <td style={printTdStyle}></td>
                                <td style={{ ...printTdStyle, fontWeight: 'bold' }}>{totalWorked.toFixed(2)}</td>
                                <td style={printTdStyle}></td>
                                <td style={{ ...printTdStyle, fontWeight: 'bold' }}>{totalWorked.toFixed(2)}</td>
                                <td style={{ ...printTdStyle, fontWeight: 'bold' }}>{totalScheduled.toFixed(2)}</td>
                                <td style={{ ...printTdStyle, fontWeight: 'bold' }}>{formatDiffHM(totalDiff)}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Signature */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px', fontSize: '14px' }}>
                        <div>Signature: _________________________________________</div>
                        <div>Date: ________________________</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── shared cell / row styles ──────────────────────────────────
const rowStyle = {
    display: 'flex',
    alignItems: 'stretch',
    minWidth: '100%',
};

const cellStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    fontSize: 12,
    color: '#333',
    borderRight: '1px solid #eaecef',
    flexShrink: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
};

const iconStyle = (color) => ({
    fontSize: 15,
    color,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
});

const popBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 16px',
    fontSize: 13,
    color: '#333',
    textAlign: 'left',
    width: '100%',
    borderRadius: 4,
};

const printThStyle = {
    padding: '8px',
    border: '1px solid #e0e0e0',
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#333',
    background: '#fcfcfc',
};

const printTdStyle = {
    padding: '8px',
    border: '1px solid #e0e0e0',
    textAlign: 'center',
    color: '#444',
};

export default AttendenceManagement;


// ============================================================
// ░░░░░░░ PREVIOUS IMPLEMENTATION (v1 & v2) – PRESERVED ░░░░░░
// ============================================================

/*
  The original filter-drawer based implementation and v2 base
  redesign are preserved here for reference.

  Key differences from v3:
  - v1/v2 used a filter drawer (property → schedule → user)
  - v1/v2 required manual search action
  - v3: auto-loads users, week selector, inline editing,
         sticky total row, hover icons on details/day columns

  See git history for full v1 source.
*/
